import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Form, Image } from 'react-bootstrap';
import { useStoreState } from 'easy-peasy';

import ButtonLoaderComponent from '../widgets/ButtonLoaderComponent';
import SelectMultipleClientsComponent from '../widgets/selectClient/SelectMultipleClientsComponent.jsx';
import { useToast } from '../../hooks/useToast.jsx';

import './PackageFormComponent.scss';

// Android application id, e.g. com.metiers360.app as it will need to be in the app manifest
const PACKAGE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
// Reserved names accepted by the API even though they don't match PACKAGE_NAME_PATTERN (e.g. the loader package)
const RESERVED_PACKAGE_NAMES = ['ADB'];

/**
 * PackageFormComponent
 * form to create a new package (headset application) or update an existing one
 * @param {callback} actionFn store thunk called with { data: FormData, uniqueId }
 * @param {Object} packageToUpdate Optionnal - Object with the infos of the package in case of an update, or null/undefined
 * @param {String} readOne  readOne slug to access a package details page - replace the :uniqueId part of string with actual uniqueId
 */
const PackageFormComponent = (props) => {
    const { actionFn, packageToUpdate, readOne: readOnePackageSlug } = props;

    const { apiData: { ACTIONS, BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);

    const { fileContraints } = BACKOFFICE_DEFAULT_VALUES;
    const token = ACTIONS?.packages?.[packageToUpdate ? 'update' : 'create']?.csrfToken;
    // APKs are matched against this name, it can't be changed once some are uploaded
    const isNameLocked = packageToUpdate?.apkCount > 0;
    // Default packages are available to every client, the API rejects any client restriction on them
    const isDefaultPackage = !!packageToUpdate?.isDefault;

    const navigate = useNavigate();
    const toast = useToast();
    const formRef = useRef(null);

    const [isSending, setIsSending] = useState(false);
    const [selectedClients, setSelectedClients] = useState(packageToUpdate?.clients?.map(client => client.uniqueId) ?? []);
    const thumbnailFileInputNameDefaultValue = packageToUpdate ? 'Changer la vignette' : 'Choisir le fichier';
    const [thumbnailFileInputName, setThumbnailFileInputName] = useState(thumbnailFileInputNameDefaultValue);
    const [formErrors, setFormErrors] = useState({ name: null, thumbnail: null });
    const [submitError, setSubmitError] = useState(null);

    const validateForm = (formData) => {
        const errors = { name: null, thumbnail: null };

        const name = formData.get('package[name]')?.trim();
        if (!name) {
            errors.name = "Veuillez saisir le nom du package";
        } else if (!isNameLocked && !RESERVED_PACKAGE_NAMES.includes(name) && !PACKAGE_NAME_PATTERN.test(name)) {
            errors.name = "Format attendu : com.exemple.application";
        }

        const thumbnail = formData.get('package[thumbnail]');
        if (thumbnail?.size) {
            if (thumbnail.size > fileContraints.MAX_SIZE_IMAGE_BYTES) {
                errors.thumbnail = "Vignette trop volumineuse";
            } else if (!fileContraints.IMAGE_MIME_TYPES.includes(thumbnail.type)) {
                errors.thumbnail = "Format de la vignette non autorisé";
            }
        }

        setFormErrors(errors);

        return !errors.name && !errors.thumbnail;
    };

    const submitForm = () => {
        const data = new FormData(formRef.current);

        if (!validateForm(data)) {
            return;
        }

        setIsSending(true);
        setSubmitError(null);

        const payload = { data };

        if (packageToUpdate?.uniqueId) {
            payload.uniqueId = packageToUpdate.uniqueId;
        }

        actionFn(payload)
            .then((result) => {
                toast.open({
                    message: packageToUpdate
                        ? "Modification de l'application réalisée avec succès"
                        : "Création de l'application réalisée avec succès",
                    variant: "success"
                });
                if (readOnePackageSlug) {
                    navigate(readOnePackageSlug.replace(':uniqueId', result.uniqueId));
                }
            })
            .catch((error) => {
                // Validation errors (duplicate name, format, default package restriction) come back as 406
                // with a displayErrorToUser message, already toasted by axios: also keep it visible in the form
                setSubmitError(typeof error?.displayErrorToUser === 'string'
                    ? error.displayErrorToUser
                    : "L'enregistrement de l'application a échoué");
            })
            .finally(() => setIsSending(false));
    };

    return (
        <div className="package-form">
            <Form ref={formRef} method="post" encType="multipart/form-data">
                <Form.Group className="formSection">
                    <Form.Label>Nom du package</Form.Label>
                    <Form.Control
                        type="text"
                        name="package[name]"
                        placeholder="com.metiers360.application"
                        defaultValue={packageToUpdate?.name ?? ''}
                        readOnly={isNameLocked}
                        isInvalid={!!formErrors.name}
                        onChange={() => setFormErrors(prev => ({ ...prev, name: null }))}
                    />
                    <Form.Text muted>
                        {isNameLocked
                            ? "Non modifiable : des APK sont déjà associés à cette application"
                            : "Doit correspondre au nom de package déclaré dans le manifest des APK"}
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="formSection">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        name="package[description]"
                        placeholder="Description de l'application"
                        defaultValue={packageToUpdate?.description ?? ''}
                    />
                </Form.Group>

                <Form.Group className="formSection">
                    <Form.Label>Vignette ({fileContraints.IMAGE_MIME_TYPES_NAMES?.join(',')}) ({fileContraints.MAX_SIZE_IMAGE_MO} Mo max.)</Form.Label>
                    <div className="input-group mb-3">
                        <div className="input-group-prepend">
                            <span className="input-group-text" id="packageThumbnailAddon">
                                {packageToUpdate?.thumbnail ? <Image src={packageToUpdate.thumbnail} rounded thumbnail className="max-width-200" /> : 'Choisir la vignette'}
                            </span>
                        </div>
                        <div className="custom-file">
                            <input
                                type="file"
                                className="custom-file-input"
                                id="packageThumbnailInput"
                                name="package[thumbnail]"
                                accept={fileContraints.IMAGE_MIME_TYPES?.join(',')}
                                aria-describedby="packageThumbnailAddon"
                                onChange={(event) => {
                                    setFormErrors(prev => ({ ...prev, thumbnail: null }));
                                    setThumbnailFileInputName(event.target.files[0]?.name ?? thumbnailFileInputNameDefaultValue);
                                }}
                            />
                            <label className="custom-file-label" htmlFor="packageThumbnailInput" data-browse="Parcourir">
                                {thumbnailFileInputName}
                            </label>
                        </div>
                    </div>
                    {formErrors.thumbnail && (
                        <div className="invalid-feedback d-block">{formErrors.thumbnail}</div>
                    )}
                </Form.Group>

                <Form.Group className="formSection">
                    <Form.Label>Client(s) associé(s)</Form.Label>
                    {isDefaultPackage
                        // No package[clients][] sent: the API treats it as an empty list, which is expected here
                        ? <Form.Text as="p" muted>Application par défaut : disponible pour tous les clients, elle ne peut pas être restreinte</Form.Text>
                        : <>
                            <Form.Text muted className="package-form-help">
                                Sans client sélectionné, l'application est disponible pour tous les clients abonnés.
                                Si des clients sont sélectionnés, elle est réservée à ces seuls clients.
                            </Form.Text>
                            <SelectMultipleClientsComponent
                                nameSelect="package[clients][]"
                                selectId="packageClientSelect"
                                selectedClients={selectedClients}
                                setSelectedClients={setSelectedClients}
                            />
                        </>
                    }
                </Form.Group>

                <Form.Control type="hidden" name="token" defaultValue={token} />
            </Form>

            {submitError && <Alert variant="danger">{submitError}</Alert>}

            <ButtonLoaderComponent
                onClick={submitForm}
                isSending={isSending}
                disabled={isSending}
                msg="Valider"
                variant="success"
            />
        </div>
    );
};

export default PackageFormComponent;
